import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { api } from "@/api";
import { LangSwitcher } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ClipboardList, Tractor, BarChart3, ShieldCheck, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/24781984/pexels-photo-24781984.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t } = useLang();
  const { enterDemo, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard", { params: { scope: "demo" } })
      .then(({ data }) => setStats(data.kpis))
      .catch(() => {});
  }, []);

  const features = [
    { icon: ClipboardList, title: t("feat_1_title"), desc: t("feat_1_desc"), color: "text-brand-red bg-red-50" },
    { icon: Tractor, title: t("feat_2_title"), desc: t("feat_2_desc"), color: "text-agri bg-agri-light" },
    { icon: BarChart3, title: t("feat_3_title"), desc: t("feat_3_desc"), color: "text-water bg-water-light" },
    { icon: ShieldCheck, title: t("feat_4_title"), desc: t("feat_4_desc"), color: "text-graphite bg-slate-100" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="flex items-center justify-between px-8 lg:px-16 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="Vaskion Gram Survey logo" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-heading font-bold text-graphite text-lg leading-tight">{t("app_name")}</div>
            <div className="text-xs text-slate-500">{t("tagline")}</div>
          </div>
        </div>
        <LangSwitcher />
      </header>

      <section className="grid lg:grid-cols-2 gap-10 items-center px-8 lg:px-16 py-16 lg:py-24 max-w-7xl mx-auto">
        <div className="animate-fade-up">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-testid="landing-kicker">
            {t("landing_kicker")}
          </div>
          <h1 className="text-4xl md:text-5xl tracking-tight font-bold text-graphite leading-[1.1] mb-6" data-testid="landing-title">
            {t("landing_title")}
          </h1>
          <p className="text-base text-slate-600 mb-10 max-w-xl leading-relaxed">{t("landing_sub")}</p>
          <div className="flex flex-wrap gap-4">
            <Button
              data-testid="explore-demo-btn"
              size="lg"
              className="bg-brand-red hover:bg-brand-reddark text-white px-8 transition-colors duration-200"
              onClick={() => {
                enterDemo();
                navigate("/dashboard");
              }}
            >
              {t("explore_demo")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              data-testid="landing-login-btn"
              size="lg"
              variant="outline"
              className="px-8 transition-colors duration-200"
              onClick={() => navigate(user ? "/dashboard" : "/login")}
            >
              {t("login")}
            </Button>
          </div>
          <div className="mt-8 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-block" data-testid="landing-demo-note">
            {t("demo_banner")}
          </div>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="rounded-xl overflow-hidden border border-slate-200">
            <img src={HERO_IMG} alt="Village fields" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-lg px-5 py-4 shadow-sm hidden lg:block" data-testid="landing-stats-chip">
            <div className="text-3xl font-heading font-bold text-agri" data-testid="landing-total-families">{stats ? stats.total_families : "…"}</div>
            <div className="text-xs text-slate-500">{t("kpi_total_families")} · K. Gopalapuram</div>
            {stats && (
              <div className="text-xs text-slate-500 mt-0.5" data-testid="landing-total-population">
                {stats.total_population} · {t("kpi_total_population")}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="px-8 lg:px-16 pb-20 max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="bg-white border border-slate-200 rounded-lg p-6 animate-fade-up hover:border-graphite/30 kpi-card"
              style={{ animationDelay: `${i * 90}ms` }}
              data-testid={`feature-card-${i}`}
            >
              <div className={`h-10 w-10 rounded-md flex items-center justify-center mb-4 ${f.color}`}>
                <f.icon className="h-5 w-5" />
              </div>
              <div className="font-heading font-semibold text-graphite mb-2">{f.title}</div>
              <div className="text-sm text-slate-500 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-100 px-8 lg:px-16 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div>{t("company")}</div>
        <div>{t("landing_footer")}</div>
      </footer>
    </div>
  );
}
